/**
 * Cookie storage operations — TTL via expires attribute; category from config.
 */

import type { MscKeyConfig } from '../types';
import { cookieValue } from '@msc-b2c/shared-utils';

const COOKIE_DEFAULTS = {
  path: '/',
  sameSite: 'Strict',
  secure: true,
} as const;

const EXPIRED_DATE = 'Thu, 01 Jan 1970 00:00:00 GMT';

function safeEncode(value: string): string {
  try {
    return encodeURIComponent(value);
  } catch {
    return value;
  }
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function getCookie(key: string): string | null {
  try {
    const v = cookieValue(key);
    if (v === undefined || v === '') return null;
    return safeDecode(v);
  } catch {
    return null;
  }
}

function getRawCookie(key: string): string | null {
  return getCookie(key);
}

function isSecureContext(): boolean {
  try {
    return window.location.protocol === 'https:';
  } catch {
    return COOKIE_DEFAULTS.secure;
  }
}

function buildCookieAttributes(): string {
  const parts: string[] = [];
  parts.push(`Path=${COOKIE_DEFAULTS.path}`);
  parts.push(`SameSite=${COOKIE_DEFAULTS.sameSite}`);
  if (isSecureContext()) parts.push('Secure');
  return parts.join('; ');
}

export function cookieStorageSet(
  cfg: MscKeyConfig,
  value: unknown,
  expireAtEpochMs: number
): boolean {
  try {
    const encoded =
      typeof value === 'string' ? safeEncode(value) : safeEncode(JSON.stringify(value));
    const expires = new Date(expireAtEpochMs).toUTCString();
    const attrs = buildCookieAttributes();
    document.cookie = `${cfg.key}=${encoded}; Expires=${expires}; ${attrs}`;
    return true;
  } catch {
    return false;
  }
}

export function cookieStorageGet(cfg: MscKeyConfig): unknown {
  const raw = getRawCookie(cfg.key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function getDomainVariants(): Array<string | undefined> {
  const variants: Array<string | undefined> = [undefined];
  const hostname = window.location.hostname;
  if (!variants.includes(hostname)) variants.push(hostname);
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const parentDomain = `.${parts.slice(-2).join('.')}`;
    if (!variants.includes(parentDomain)) variants.push(parentDomain);
    const parentDomainNoDot = parts.slice(-2).join('.');
    if (!variants.includes(parentDomainNoDot)) variants.push(parentDomainNoDot);
  }
  return variants;
}

function buildDeletionString(key: string, path: string, domain?: string): string {
  let str = `${key}=; Expires=${EXPIRED_DATE}; Path=${path}`;
  if (domain) str += `; Domain=${domain}`;
  return str;
}

function deleteCookieAggressive(key: string): void {
  const path = COOKIE_DEFAULTS.path;
  const domainVariants = getDomainVariants();
  for (const domain of domainVariants) {
    document.cookie = buildDeletionString(key, path, domain);
  }
}

interface CookieInfo {
  name: string;
  value: string;
  domain: string | null;
  path: string;
}

function hasCookieStoreAPI(): boolean {
  return (
    typeof window !== 'undefined' &&
    'cookieStore' in window &&
    typeof (
      window as Window & { cookieStore?: { getAll: (name: string) => Promise<CookieInfo[]> } }
    ).cookieStore?.getAll === 'function'
  );
}

async function deleteCookieWithCookieStore(key: string): Promise<boolean> {
  if (!hasCookieStoreAPI()) return false;
  try {
    const store = (
      window as unknown as {
        cookieStore: {
          getAll: (name: string) => Promise<CookieInfo[]>;
          delete: (opts: { name: string; path?: string; domain?: string | null }) => Promise<void>;
        };
      }
    ).cookieStore;
    const cookies = (await store.getAll(key)) as CookieInfo[];
    if (cookies.length === 0) return false;
    for (const cookie of cookies) {
      const deleteOptions: { name: string; path?: string; domain?: string | null } = {
        name: cookie.name,
        path: cookie.path,
      };
      if (cookie.domain) deleteOptions.domain = cookie.domain;
      await store.delete(deleteOptions);
    }
    return true;
  } catch {
    return false;
  }
}

export function cookieStorageRemove(cfg: MscKeyConfig): void {
  try {
    deleteCookieAggressive(cfg.key);
  } catch {
    console.log('error deleting cookie', cfg.key);
  }
  deleteCookieWithCookieStore(cfg.key).catch(() => {});
}

export function cookieRemoveByKey(key: string): void {
  try {
    deleteCookieAggressive(key);
  } catch {
    console.log('error deleting cookie', key);
  }
  deleteCookieWithCookieStore(key).catch(() => {});
}

export function cookieStorageExists(cfg: MscKeyConfig): boolean {
  return getRawCookie(cfg.key) !== null;
}

export function parseCookieNames(): string[] {
  try {
    const cookieStr = document.cookie;
    if (!cookieStr) return [];
    return cookieStr
      .split(';')
      .map((c) => c.trim().split('=')[0])
      .filter(Boolean);
  } catch {
    return [];
  }
}
